/**
 * Hook for on-demand order status checking
 * Replaces automatic polling with user-triggered status checks
 * Provides simple API for checking individual order status
 */

import {useSecureApi} from '@hconnect/common/hooks/useApi'
import {useState, useCallback} from 'react'

import {TransactionResultType} from '../types/confirmationResult'
import {
  mapRmcOrderRequestStatus,
  RmcOrderRequestStatusData
} from '../utils/mapRmcOrderRequestStatus'

export interface OrderStatusCheckResult {
  status: TransactionResultType | null
  lastChecked: Date | null
  error: string | null
  sapResponse?: unknown
}

/**
 * Hook for checking individual order status on demand
 * @returns Object with checkOrderStatus function and current state
 */
export const useOrderStatusCheck = () => {
  const secureApi = useSecureApi()
  const [isLoading, setIsLoading] = useState(false)
  const [lastResult, setLastResult] = useState<OrderStatusCheckResult | null>(null)

  const checkOrderStatus = useCallback(
    async (requestId: string): Promise<OrderStatusCheckResult> => {
      setIsLoading(true)

      try {
        console.log(`Checking order status for: ${requestId}`)

        const response = await secureApi.get<RmcOrderRequestStatusData>(
          `/orderRequests/${requestId}`
        )
        const data = response.data

        if (data) {
          const transactionStatus = mapRmcOrderRequestStatus(data)

          const result: OrderStatusCheckResult = {
            status: transactionStatus,
            lastChecked: new Date(),
            error: null,
            sapResponse: data
          }

          setLastResult(result)
          console.log(`Order ${requestId} status: ${transactionStatus}`)

          return result
        }

        // No data received
        const result: OrderStatusCheckResult = {
          status: 'pending',
          lastChecked: new Date(),
          error: null
        }

        setLastResult(result)
        return result
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        console.error(`Error checking order status for ${requestId}:`, error)

        const result: OrderStatusCheckResult = {
          status: null,
          lastChecked: new Date(),
          error: errorMessage
        }

        setLastResult(result)
        return result
      } finally {
        setIsLoading(false)
      }
    },
    [secureApi]
  )

  return {
    checkOrderStatus,
    isLoading,
    lastResult
  }
}
